if (!customElements.get('hero-slideshow')) {
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.isReady) return;

      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.liveRegion = this.querySelector('[data-live-region]');
      this.currentIndex = 0;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10) || 6000;
      this.fadeDuration = 1100;
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visibilityObserver = null;
      this.autoplayTimer = null;
      this.leaveTimer = null;
      this.pointerStartX = 0;
      this.pointerDeltaX = 0;
      this.isPointerDown = false;
      this.activePointerId = null;
      this.isFocusedWithin = false;
      this.isHovered = false;

      if (!this.track || this.slides.length === 0) {
        return;
      }

      this.isReady = true;
      this.style.setProperty('--hero-dot-progress-duration', `${this.autoplaySpeed}ms`);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onReducedMotionChange = this.handleReducedMotionChange.bind(this);
      this.onMouseEnter = this.handleMouseEnter.bind(this);
      this.onMouseLeave = this.handleMouseLeave.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.handleBlockDeselect.bind(this);
      this.onPrevClick = this.handlePrevClick.bind(this);
      this.onNextClick = this.handleNextClick.bind(this);

      this.dotHandlers = new Map();

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);

      this.dots.forEach((dot) => {
        const dotHandler = () => {
          const nextIndex = Number.parseInt(dot.dataset.index, 10);
          this.goTo(nextIndex, { announce: true, userInitiated: true });
        };

        this.dotHandlers.set(dot, dotHandler);
        dot.addEventListener('click', dotHandler);
      });

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('mouseenter', this.onMouseEnter);
      this.viewport.addEventListener('mouseleave', this.onMouseLeave);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (typeof this.reduceMotionQuery.addEventListener === 'function') {
        this.reduceMotionQuery.addEventListener('change', this.onReducedMotionChange);
      } else if (typeof this.reduceMotionQuery.addListener === 'function') {
        this.reduceMotionQuery.addListener(this.onReducedMotionChange);
      }

      this.goTo(0, { announce: false, userInitiated: false });
      this.setupEntranceAnimation();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.leaveTimer) {
        window.clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);

      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);

      this.dots.forEach((dot) => {
        const dotHandler = this.dotHandlers?.get(dot);

        if (dotHandler) {
          dot.removeEventListener('click', dotHandler);
        }
      });

      if (this.viewport) {
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        this.viewport.removeEventListener('pointerup', this.onPointerUp);
        this.viewport.removeEventListener('pointercancel', this.onPointerUp);
        this.viewport.removeEventListener('mouseenter', this.onMouseEnter);
        this.viewport.removeEventListener('mouseleave', this.onMouseLeave);
      }

      if (this.reduceMotionQuery) {
        if (typeof this.reduceMotionQuery.removeEventListener === 'function') {
          this.reduceMotionQuery.removeEventListener('change', this.onReducedMotionChange);
        } else if (typeof this.reduceMotionQuery.removeListener === 'function') {
          this.reduceMotionQuery.removeListener(this.onReducedMotionChange);
        }
      }

      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }

      this.isReady = false;
    }

    setupEntranceAnimation() {
      if (this.reduceMotionQuery.matches) {
        this.classList.add('is-visible');
        return;
      }

      if (!('IntersectionObserver' in window)) {
        window.requestAnimationFrame(() => {
          this.classList.add('is-visible');
        });

        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('is-visible');
              this.visibilityObserver?.disconnect();
              this.visibilityObserver = null;
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: '0px 0px -5% 0px'
        }
      );

      this.visibilityObserver.observe(this);
    }

    handlePointerDown(event) {
      if (this.slides.length < 2 || event.isPrimary === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, label')) return;

      this.isPointerDown = true;
      this.activePointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerDeltaX = 0;
      this.classList.add('is-dragging');
      this.stopAutoplay();

      if (typeof this.viewport.setPointerCapture === 'function') {
        this.viewport.setPointerCapture(event.pointerId);
      }

      if (event.pointerType === 'mouse') {
        event.preventDefault();
      }
    }

    handlePointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;
      this.pointerDeltaX = event.clientX - this.pointerStartX;
    }

    handlePointerUp(event) {
      if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

      const swipeThreshold = 42;

      if (Math.abs(this.pointerDeltaX) > swipeThreshold) {
        if (this.pointerDeltaX < 0) {
          this.goTo(this.currentIndex + 1, { announce: true, userInitiated: true });
        } else {
          this.goTo(this.currentIndex - 1, { announce: true, userInitiated: true });
        }
      } else {
        this.startAutoplay();
      }

      this.isPointerDown = false;
      this.activePointerId = null;
      this.pointerStartX = 0;
      this.pointerDeltaX = 0;
      this.classList.remove('is-dragging');

      if (
        typeof this.viewport.releasePointerCapture === 'function' &&
        typeof this.viewport.hasPointerCapture === 'function' &&
        this.viewport.hasPointerCapture(event.pointerId)
      ) {
        this.viewport.releasePointerCapture(event.pointerId);
      }
    }

    handleKeydown(event) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.goTo(this.currentIndex - 1, { announce: true, userInitiated: true });
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.goTo(this.currentIndex + 1, { announce: true, userInitiated: true });
          break;
        case 'Home':
          event.preventDefault();
          this.goTo(0, { announce: true, userInitiated: true });
          break;
        case 'End':
          event.preventDefault();
          this.goTo(this.slides.length - 1, { announce: true, userInitiated: true });
          break;
        default:
          break;
      }
    }

    handleFocusIn() {
      this.isFocusedWithin = true;
      this.stopAutoplay();
    }

    handleFocusOut() {
      window.requestAnimationFrame(() => {
        this.isFocusedWithin = this.contains(document.activeElement);

        if (!this.isFocusedWithin) {
          this.startAutoplay();
        }
      });
    }

    handlePrevClick() {
      this.goTo(this.currentIndex - 1, { announce: true, userInitiated: true });
    }

    handleNextClick() {
      this.goTo(this.currentIndex + 1, { announce: true, userInitiated: true });
    }

    handleBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.stopAutoplay();
      this.goTo(index, { announce: false, userInitiated: false });
    }

    handleBlockDeselect(event) {
      if (!this.slides.includes(event.target)) return;
      this.startAutoplay();
    }

    handleMouseEnter() {
      this.isHovered = true;
      this.stopAutoplay();
    }

    handleMouseLeave() {
      this.isHovered = false;
      this.startAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.isHovered = false;
        this.isFocusedWithin = false;
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleReducedMotionChange() {
      if (this.reduceMotionQuery.matches) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    goTo(index, { announce = false, userInitiated = false } = {}) {
      if (!this.slides.length) return;

      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      const previousIndex = this.currentIndex;

      if (this.leaveTimer) {
        window.clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      }

      this.slides.forEach((slide) => {
        slide.classList.remove('is-leaving');
      });

      this.currentIndex = normalizedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.classList.toggle('is-active', isActive);
      });

      if (previousIndex !== normalizedIndex && this.slides[previousIndex]) {
        const leavingSlide = this.slides[previousIndex];
        leavingSlide.classList.add('is-leaving');

        this.leaveTimer = window.setTimeout(() => {
          leavingSlide.classList.remove('is-leaving');
          this.leaveTimer = null;
        }, this.reduceMotionQuery.matches ? 20 : this.fadeDuration);
      }

      this.dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', String(dotIndex === normalizedIndex));
      });

      this.restartDotProgress();

      if (announce && this.liveRegion) {
        const activeSlide = this.slides[normalizedIndex];
        const heading = activeSlide.querySelector('.hero-slideshow__heading');
        const headingText = heading ? heading.textContent.trim() : `Slide ${normalizedIndex + 1}`;
        this.liveRegion.textContent = `${headingText}. Slide ${normalizedIndex + 1} of ${this.slides.length}.`;
      }

      if (userInitiated) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.slides.length < 2 ||
        this.reduceMotionQuery.matches ||
        document.hidden ||
        this.isFocusedWithin ||
        this.isHovered
      ) {
        return;
      }

      this.stopAutoplay();
      this.restartDotProgress();
      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.currentIndex + 1, { announce: false, userInitiated: false });
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      this.clearDotProgress();
    }

    restartDotProgress() {
      this.clearDotProgress();

      if (!this.shouldAnimateDotProgress()) {
        return;
      }

      const activeDot = this.dots[this.currentIndex];

      if (!activeDot) return;

      // Force reflow so the progress animation restarts on every slide change.
      void activeDot.offsetWidth;
      activeDot.classList.add('is-progressing');
    }

    clearDotProgress() {
      this.dots.forEach((dot) => {
        dot.classList.remove('is-progressing');
      });
    }

    shouldAnimateDotProgress() {
      return (
        this.autoplayEnabled &&
        this.slides.length > 1 &&
        !this.reduceMotionQuery.matches &&
        !document.hidden &&
        !this.isFocusedWithin &&
        !this.isHovered
      );
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
}